<template>
  <overview-table
    title="Quadro Resumo - Emitidos"
    :items="items"
    :headers="headers"
    :footers="[footer]"
    expand-on-click
  >
    <template #[`item.price`]="{ item }">
      <span>{{ currency(item.price) }}</span>
    </template>
    <template #[`item.revenue`]="{ item }">
      <span>{{ currency(item.revenue) }}</span>
    </template>
    <template #expanded-row="{ item }">
      <tr v-for="(child, i) in item.children" :key="i" style="background-color: #f5f5f5">
        <td>{{ child.title }}</td>
        <td>{{ child.type }}</td>
        <td>{{ child.batch }}</td>
        <td>{{ child.generated }}</td>
        <td>{{ child.read }}</td>
        <td>{{ currency(child.price) }}</td>
        <td>{{ currency(child.revenue) }}</td>
      </tr>
    </template>
    <template #no-data>
      <h4>Não há dados disponíveis no momento.</h4>
    </template>
  </overview-table>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import type Overview from '@/types/Overview'
import { currency } from '@/helpers/formatters/currency'
import { FreeTypes } from '@/constants/TicketTypeNames'
import { groupBy, partition } from '@/helpers/array'
import OverviewTable from './partials/OverviewTable.vue'

interface TableItem {
  id: number
  section_id: string
  title: string
  type: string
  price: number
  generated: number
  read: number
  revenue: number
  batch: number
  children: TableItem[]
}

const { overview } = defineProps<{ overview: Overview }>()

// --- Items ---
const items = computed<TableItem[]>(() => {
  if (overview.summary.length === 0) {
    return []
  }

  const items = new Map<string, TableItem>()

  for (const item of overview.summary) {
    const key = `${item.section_id}#${item.ticket_type_id}#${item.batch}`

    let current = items.get(key)

    if (!current) {
      current = createBaseItem({
        section_id: item.section_id,
        title: `${item.sector_name} - ${item.section_name}`,
        type: item.ticket_type_name,
        batch: item.batch,
        price: parseFloat(item.price),
      })
      items.set(key, current)
    }

    current.read += item.read
    current.generated += item.generated
    current.revenue += (parseFloat(item.price) * item.generated) - parseFloat(item.discounts)
  }

  const [free, result] = partition([ ...items.values() ], item => FreeTypes.includes(item.type))

  const freeGroups = groupBy<TableItem, string>(free, item => item.section_id)

  for (const group of Object.values(freeGroups)) {
    const parent = createBaseItem({
      ...group[0],
      type: 'Não pagantes',
      read: 0,
      generated: 0,
      revenue: 0,
      children: [],
    })

    group.forEach((item) => {
      parent.generated += item.generated
      parent.read += item.read
      parent.revenue += item.revenue
      parent.children.push(item)
    })

    result.push(parent)
  }

  return result.sort((a, b) => {
    return a.title.localeCompare(b.title)
      || (a.type == 'Não Pagantes' ? 0 : -1)
  })
})

function createBaseItem(override: Partial<TableItem> = {}): TableItem {
  return {
    id: Math.random(), // 1 in 2e53? we take those
    section_id: '',
    title: '',
    type: '',
    price: 0,
    read: 0,
    generated: 0,
    revenue: 0,
    batch: 1,
    children: [],
    ...override,
  }
}

const generated = computed(() => items.value.reduce((acc, curr) => acc + curr.generated, 0))

const read = computed(() => items.value.reduce((acc, curr) => acc + curr.read, 0))

const revenue = computed(() => items.value.reduce((acc, curr) => acc + curr.revenue, 0))

// --- Header ---
const headers = computed(() => items.value.length > 0 ? _headers.value : [])

const _headers = ref([
  { key: 'title', value: 'title', title: 'Setor', sortable: true },
  { key: 'type', value: 'type', title: 'Tipo', sortable: true },
  { key: 'batch', value: 'batch', title: 'Lote', sortable: true },
  { key: 'generated', value: 'generated', title: 'Emitidos', sortable: true },
  { key: 'read', value: 'read', title: 'Computados', sortable: true },
  { key: 'price', value: 'price', title: 'Valor Unitário', sortable: true },
  { key: 'revenue', value: 'revenue', title: 'Arrecadação', sortable: true },
])

// --- Footer ---
const footer = computed(
  () => ({
    title: 'Total',
    generated: generated.value,
    read: read.value,
    revenue: currency(revenue.value),
  }),
)

</script>
